import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, Check, Building2, UtensilsCrossed, Home, Calendar, ShoppingBag, Briefcase, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { publicAPI, productsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';

const iconMap = {
  Building2,
  UtensilsCrossed,
  Home,
  Calendar,
  ShoppingBag,
  Briefcase,
};

export function UseCasesPage() {
  const { t, language } = useLanguage();
  const { slug } = useParams();
  const [useCases, setUseCases] = useState([]);
  const [currentUseCase, setCurrentUseCase] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUseCases();
  }, []);

  useEffect(() => {
    if (slug && useCases.length > 0) {
      loadUseCase(slug);
    } else if (!slug && useCases.length > 0) {
      setCurrentUseCase(null);
      setLoading(false);
    }
  }, [slug, useCases]);

  const loadUseCases = async () => {
    try {
      const res = await publicAPI.getUseCases();
      setUseCases(res.data || []);
    } catch (error) {
      console.error('Failed to load use cases:', error);
    }
  };

  const loadUseCase = async (ucSlug) => {
    setLoading(true);
    try {
      const uc = useCases.find(u => u.slug === ucSlug);
      if (uc) {
        setCurrentUseCase(uc);
        // Load related products
        if (uc.products?.length > 0) {
          const prodRes = await productsAPI.getAll({});
          const related = prodRes.data?.products?.filter(p => uc.products.includes(p.product_id)) || [];
          setRelatedProducts(related);
        }
      }
    } catch (error) {
      console.error('Failed to load use case:', error);
    } finally {
      setLoading(false);
    }
  };

  // List view
  if (!slug) {
    return (
      <div data-testid="usecases-list-page">
        <div className="bg-[#F8FAFC] py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 font-['Outfit']">
              {t('usecases.title')}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('usecases.subtitle')}
            </p>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {useCases.map((uc, index) => {
              const IconComponent = iconMap[uc.icon] || Building2;
              return (
                <Link
                  key={uc.usecase_id}
                  to={`/cas-usage/${uc.slug}`}
                  className="group border border-border hover:border-[#1F2937] transition-all duration-300 animate-slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`usecase-card-${uc.slug}`}
                >
                  {uc.image && (
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={uc.image}
                        alt={language === 'fr' ? uc.title_fr : uc.title_en}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <IconComponent className="h-6 w-6 text-[#DC2626]" />
                      <h2 className="text-xl font-semibold font-['Outfit']">
                        {language === 'fr' ? uc.title_fr : uc.title_en}
                      </h2>
                    </div>
                    <p className="text-muted-foreground line-clamp-3 mb-4">
                      {language === 'fr' ? uc.description_fr : uc.description_en}
                    </p>
                    <span className="inline-flex items-center text-sm font-medium text-[#DC2626] group-hover:gap-2 transition-all">
                      {language === 'fr' ? 'En savoir plus' : 'Learn more'}
                      <ArrowRight className="h-4 w-4 ml-1" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Detail view
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <Skeleton className="h-64 mb-8" />
        <Skeleton className="h-8 w-1/2 mb-4" />
        <Skeleton className="h-32" />
      </div>
    );
  }

  if (!currentUseCase) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 text-center">
        <p className="text-muted-foreground mb-4">
          {language === 'fr' ? 'Cas d\'usage non trouvé' : 'Use case not found'}
        </p>
        <Link to="/cas-usage">
          <Button variant="outline">{t('common.back')}</Button>
        </Link>
      </div>
    );
  }

  const IconComponent = iconMap[currentUseCase.icon] || Building2;
  const benefits = language === 'fr' ? currentUseCase.benefits_fr : currentUseCase.benefits_en;

  return (
    <div data-testid="usecase-detail-page">
      {/* Breadcrumb */}
      <div className="bg-secondary border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-muted-foreground hover:text-[#1F2937]">
              {t('nav.home')}
            </Link>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <Link to="/cas-usage" className="text-muted-foreground hover:text-[#1F2937]">
              {t('nav.usecases')}
            </Link>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <span className="text-[#1F2937]">
              {language === 'fr' ? currentUseCase.title_fr : currentUseCase.title_en}
            </span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="relative bg-[#1F2937] text-white">
        {currentUseCase.image && (
          <div className="absolute inset-0 opacity-30">
            <img src={currentUseCase.image} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="flex items-center gap-4 mb-6">
            <IconComponent className="h-10 w-10 text-[#DC2626]" />
            <h1 className="text-4xl md:text-5xl font-bold font-['Outfit']">
              {language === 'fr' ? currentUseCase.title_fr : currentUseCase.title_en}
            </h1>
          </div>
          <p className="text-xl text-gray-300 max-w-3xl">
            {language === 'fr' ? currentUseCase.description_fr : currentUseCase.description_en}
          </p>
        </div>
      </div>

      {/* Benefits */}
      {benefits?.length > 0 && (
        <section className="py-16 md:py-24 bg-[#F8FAFC]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-12 text-center font-['Outfit']">
              {t('usecases.benefits')}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 p-6 bg-white border border-border animate-slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-8 h-8 flex-shrink-0 bg-[#DC2626]/10 flex items-center justify-center">
                    <Check className="h-5 w-5 text-[#DC2626]" />
                  </div>
                  <p className="font-medium">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-12 text-center font-['Outfit']">
              {t('usecases.relatedProducts')}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {relatedProducts.map((product, index) => (
                <Link
                  key={product.product_id}
                  to={`/produit/${product.slug}`}
                  className="group product-card border border-border animate-slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="aspect-square bg-secondary overflow-hidden">
                    {product.images?.[0] && (
                      <img
                        src={product.images[0]}
                        alt={language === 'fr' ? product.name_fr : product.name_en}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="font-semibold mb-2">
                      {language === 'fr' ? product.name_fr : product.name_en}
                    </h3>
                    <span className="product-cta inline-flex items-center text-sm font-medium text-[#DC2626]">
                      {t('products.requestQuote')}
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 md:py-24 bg-[#1F2937] text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 font-['Outfit']">
            {language === 'fr' ? 'Prêt à digitaliser votre activité ?' : 'Ready to digitize your business?'}
          </h2>
          <p className="text-gray-400 mb-8">
            {language === 'fr'
              ? 'Contactez-nous pour discuter de votre projet NFC personnalisé.'
              : 'Contact us to discuss your custom NFC project.'}
          </p>
          <Link to="/devis">
            <Button
              size="lg"
              className="bg-[#DC2626] hover:bg-[#DC2626]/90 text-white rounded-none uppercase tracking-widest text-xs px-8 py-6 h-auto"
            >
              {t('hero.cta.quote')}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}

export default UseCasesPage;
